import type { CSSProperties, ReactNode } from 'react';
import type { BlockField, BlockFieldStyle } from '@/blocks/domain/types';
import { normalizeImageMediaSize, normalizeMediaSize } from '@/blocks/domain/types';
import { assertSafeImageUrl } from '@/common/domain/utils/assertSafeImageUrl';
import { imageHtmlStyle } from '@/document-render/application/imageLayout';
import { logoImageStyle, logoJustifyContent } from '@/document-render/application/logoLayout';
import { fieldTextStyleOf } from '@/blocks/domain/helpers/fieldTextStyle';
import { sanitizeProseHtml } from '@/document-render/application/sanitizeProseHtml';
import { chipUnboundTokensInHtml } from '@/document-render/application/unboundTokenHtml';
import { BORDER_COLORS, TEXT_COLORS } from '@/common/presentation/constants/colors';
import { fieldsHeadingFontSizePx, FIELDS_HEADING_LETTER_SPACING_EM } from '@/document-render/application/fieldsBlockTypography';
import { EDITOR_FIELD_TEXT_METRICS, FIELD_LIST_LABEL_VALUE_GAP_PX, type FieldListTextMetrics } from '@/common/domain/fieldListSpacing';

export interface FieldElementVisual {
  accent: string;
  fontSize: number;
  /** Numeric CSS weight. Absent keeps the heading at 700. */
  fontWeight?: number;
}

export interface FieldElementRenderProps {
  field: BlockField;
  /** Already token-resolved and trimmed. */
  resolvedValue: string;
  /** Drawn in place of `resolvedValue` by the text styles (editor-only unbound token chips). */
  valueDisplay?: ReactNode;
  /** `rich_text` only (editor, Sample data off): `{{tokens}}` kept in `resolvedValue` are drawn as chips. */
  chipTokens?: boolean;
  visual: FieldElementVisual;
  /** Resolved company logo URL, for `style: 'logo'` fields — independent of the block's own header-chrome logo. */
  companyLogoUrl?: string | null;
  /** Defaults to the editor preview. Export mode passes the pre-373a9a4 PDF metrics. */
  metrics?: FieldListTextMetrics;
}

const SUBHEADING_FONT_RATIO = 0.86;

function withOptionalLength(style: CSSProperties, lengths: { lineHeight?: number; minHeight?: number }): CSSProperties {
  const next: CSSProperties = { ...style };
  if (lengths.lineHeight != null) next.lineHeight = lengths.lineHeight;
  if (lengths.minHeight != null) next.minHeight = lengths.minHeight;
  return next;
}

function rowJustify(align: BlockField['align']): 'flex-start' | 'center' | 'flex-end' {
  if (align === 'right') return 'flex-end';
  if (align === 'center') return 'center';
  return 'flex-start';
}

function headingBlock(text: ReactNode, accent: string, fontSize: number, fontWeight = 700): ReactNode {
  return (
    <div
      style={{
        fontSize: fieldsHeadingFontSizePx(fontSize),
        fontWeight,
        color: accent,
        letterSpacing: `${FIELDS_HEADING_LETTER_SPACING_EM}em`,
        textTransform: 'uppercase'
      }}
    >
      {text}
    </div>
  );
}

function mediaBoxStyle(field: BlockField): CSSProperties {
  return imageHtmlStyle(normalizeImageMediaSize(field.media?.size));
}

function renderImage(field: BlockField, resolvedValue: string, metrics: FieldListTextMetrics): ReactNode {
  const url = assertSafeImageUrl(resolvedValue || field.value);
  if (!url) return null;
  const alignment = field.media?.alignment ?? field.align ?? 'left';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: logoJustifyContent(alignment) }}>
      <img src={url} alt={field.media?.altText ?? ''} style={mediaBoxStyle(field)} />
      {field.media?.description ? (
        <div style={{ fontSize: metrics.imageCaptionFontSize, color: TEXT_COLORS.MUTED, marginTop: metrics.imageCaptionMarginTop }}>
          {field.media.description}
        </div>
      ) : null}
    </div>
  );
}

function renderLogo(field: BlockField, companyLogoUrl: string | null | undefined): ReactNode {
  const url = assertSafeImageUrl(companyLogoUrl);
  if (!url) return null;
  const size = normalizeMediaSize(field.media?.size);
  const alignment = field.media?.alignment ?? field.align ?? 'left';
  return (
    <div style={{ display: 'flex', justifyContent: logoJustifyContent(alignment) }}>
      <img src={url} alt="Company logo" style={logoImageStyle(size)} />
    </div>
  );
}

/** Whether a resolved field value counts as present (an em dash means "known path, no value"). */
export function hasFieldValue(resolved: string): boolean {
  return resolved !== '' && resolved !== '—';
}

/**
 * One case per `BlockFieldStyle` (LLAMS-610 Part 7 — the 12-value element model). Mirrors on the
 * PDF side in `document-pdf/application/react-pdf/blocks/fields/FieldElementPdf.tsx`; keep both in
 * sync — see the HTML/PDF parity rule in `.cursor/plans/README.md`.
 */
export function renderFieldElement(props: FieldElementRenderProps): ReactNode {
  const element = renderFieldElementBase(props);
  const textStyle = fieldTextStyleOf(props.field);
  if (element === null || textStyle === null) return element;
  // The Style panel's switches: one wrapper, only when a switch is on, so every other element is untouched.
  return (
    <div style={{ fontStyle: textStyle.italic ? 'italic' : undefined, textTransform: textStyle.uppercase ? 'uppercase' : undefined }}>
      {element}
    </div>
  );
}

function renderFieldElementBase(props: FieldElementRenderProps): ReactNode {
  const { field, resolvedValue, valueDisplay, chipTokens = false, visual, companyLogoUrl, metrics = EDITOR_FIELD_TEXT_METRICS } = props;
  const hasValue = valueDisplay != null || hasFieldValue(resolvedValue);
  const display: ReactNode = valueDisplay ?? resolvedValue;
  const style: BlockFieldStyle = field.style;

  switch (style) {
    case 'title':
    case 'heading':
      return hasValue || field.label
        ? headingBlock(hasValue ? display : field.label, visual.accent, visual.fontSize, visual.fontWeight)
        : null;

    case 'subheading':
      return hasValue || field.label ? (
        <div style={{ fontSize: Math.round(visual.fontSize * SUBHEADING_FONT_RATIO), fontWeight: 600, color: visual.accent }}>
          {hasValue ? display : field.label}
        </div>
      ) : null;

    case 'value':
      return hasValue ? <div>{display}</div> : null;

    case 'text':
    case 'label_value': {
      if (!hasValue && !field.label) return null;
      const labelVisible = field.labelVisible !== false;
      const valueVisible = field.valueVisible !== false;
      const sideBySide = style === 'label_value' && field.orientation !== 'vertical';
      return (
        <div
          style={
            sideBySide
              ? {
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: rowJustify(field.align),
                  alignItems: 'baseline',
                  gap: FIELD_LIST_LABEL_VALUE_GAP_PX,
                  width: '100%'
                }
              : undefined
          }
        >
          {labelVisible && field.label ? (
            <div
              style={withOptionalLength(
                {
                  fontSize: metrics.labelFontSize,
                  color: TEXT_COLORS.MUTED,
                  marginBottom: sideBySide ? 0 : metrics.labelMarginBottom
                },
                { lineHeight: metrics.labelLineHeight }
              )}
            >
              {field.label}
            </div>
          ) : null}
          {valueVisible ? (
            <div style={withOptionalLength({}, { minHeight: metrics.valueMinHeight, lineHeight: metrics.valueLineHeight })}>
              {hasValue ? display : ' '}
            </div>
          ) : null}
        </div>
      );
    }

    case 'caption':
      if (!hasValue && !field.label) return null;
      return (
        <div>
          <div style={{ borderBottom: `1px solid ${BORDER_COLORS.MUTED}`, marginBottom: metrics.captionRuleMarginBottom }} />
          <div style={{ fontSize: metrics.captionFontSize, color: TEXT_COLORS.MUTED }}>{hasValue ? display : field.label}</div>
        </div>
      );

    case 'signature_line':
      if (!hasValue && !field.label) return null;
      return (
        <div>
          <div
            style={{
              borderBottom: `1px solid ${BORDER_COLORS.MUTED}`,
              height: metrics.signatureLineHeight,
              marginBottom: metrics.captionRuleMarginBottom
            }}
          />
          <div
            style={withOptionalLength(
              { fontSize: metrics.captionFontSize, color: TEXT_COLORS.MUTED },
              { lineHeight: metrics.signatureCaptionLineHeight }
            )}
          >
            {field.label || resolvedValue}
          </div>
        </div>
      );

    case 'image':
      return renderImage(field, resolvedValue, metrics);

    case 'logo':
      return renderLogo(field, companyLogoUrl);

    case 'rich_text':
      if (!resolvedValue) return null;
      // Only `rich_text` trusts its value as HTML (TipTap-authored, tokens resolved) — every other
      // style renders plain text. Same pattern as the existing `ProseRender.tsx`; `react/no-danger`
      // is not configured in this repo, so no disable directive is needed (would be an unused one).
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: chipTokens ? chipUnboundTokensInHtml(sanitizeProseHtml(resolvedValue)) : sanitizeProseHtml(resolvedValue)
          }}
        />
      );

    case 'dataset':
      // Placement-only marker: this field family (Header/Customer/Signature) never carries bound
      // dataset content — a dataset's own block (line items, roles, price book) draws it.
      return null;
  }

  return null;
}
